import { Field, FieldArray, useField } from 'formik'
import type { FieldProps } from 'formik'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import {
  CANDIDATE_FIELD_OPTIONS,
  COMPARISON_OPERATOR_OPTIONS,
  VALIDATION_RULE_TYPE_OPTIONS
} from '../questionFormOptions'
import type { ValidationRuleFormValue } from '../addQuestionModal/AddQuestionModal.types'

import { Button, Flex, FormField, Select, TextInput } from '@percent/lemonade'

const generateUid = () => crypto.randomUUID()

const RuleCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.gray90};
  border-radius: ${({ theme }) => theme.borderRadii.medium};
  padding: ${({ theme }) => theme.sizes[3]};
`

function isValidPositiveNumber(value: string): boolean {
  if (value === '') return true
  const num = Number(value)

  return Number.isFinite(num) && num > 0
}

function PercentageField({ index, testIdPrefix }: { index: number; testIdPrefix: string }) {
  const { t } = useTranslation()
  const [field, , helpers] = useField<number | undefined>(`validationRules.${index}.percentage`)
  const [displayValue, setDisplayValue] = useState(() => (field.value !== undefined ? String(field.value) : ''))
  const [error, setError] = useState<string | undefined>()
  const lastFormikValue = useRef(field.value)

  useEffect(() => {
    if (field.value !== lastFormikValue.current) {
      lastFormikValue.current = field.value
      setDisplayValue(field.value !== undefined ? String(field.value) : '')
      setError(undefined)
    }
  }, [field.value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    setDisplayValue(raw)

    if (raw === '') {
      const next = undefined
      lastFormikValue.current = next
      helpers.setValue(next)
      setError(undefined)
    } else if (isValidPositiveNumber(raw)) {
      const next = Number(raw)
      lastFormikValue.current = next
      helpers.setValue(next)
      setError(undefined)
    } else {
      lastFormikValue.current = undefined
      helpers.setValue(undefined)
      setError(t('errorMessage.mustBePositiveNumber'))
    }
  }

  return (
    <div style={{ flex: 1, minWidth: 100 }}>
      <FormField
        label={t('grants.forms.questions.validationRules.percentage')}
        description={t('grants.forms.questions.validationRules.percentageHelp')}
        status={error ? 'danger' : 'default'}
        statusMessage={error}
      >
        <TextInput
          data-testid={`${testIdPrefix}-validation-rule-percentage-${index}`}
          placeholder="e.g. 110"
          name={field.name}
          value={displayValue}
          onChange={handleChange}
          onBlur={field.onBlur}
          status={error ? 'danger' : 'default'}
        />
      </FormField>
    </div>
  )
}

type ValidationRulesSectionProps = {
  validationRules: ValidationRuleFormValue[]
  testIdPrefix: string
}

export function ValidationRulesSection({ validationRules, testIdPrefix }: Readonly<ValidationRulesSectionProps>) {
  const { t } = useTranslation()

  return (
    <FieldArray name="validationRules">
      {({ push, remove }) => (
        <FormField label={t('grants.forms.questions.validationRules.label')} necessity="optional">
          <Flex direction="column" gap="sm">
            {validationRules.map((rule, index) => (
              <RuleCard key={rule.uid}>
                <Flex gap="sm" align="flex-start" wrap="wrap">
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <Field name={`validationRules.${index}.type`}>
                      {({ field, meta, form }: FieldProps<string>) => (
                        <FormField
                          label={t('grants.forms.questions.validationRules.type')}
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <Select
                            data-testid={`${testIdPrefix}-validation-rule-type-${index}`}
                            name={field.name}
                            options={VALIDATION_RULE_TYPE_OPTIONS}
                            defaultValue={VALIDATION_RULE_TYPE_OPTIONS.find(o => o.value === field.value)}
                            onChange={e => {
                              form.setFieldValue(field.name, e.value)
                              form.setFieldValue(`validationRules.${index}.candidateField`, '')
                              form.setFieldValue(`validationRules.${index}.value`, '')
                              form.setFieldValue(`validationRules.${index}.percentage`, undefined)
                            }}
                          />
                        </FormField>
                      )}
                    </Field>
                  </div>

                  <div style={{ flex: 1, minWidth: 160 }}>
                    {rule.type === 'value_comparison' ? (
                      <Field name={`validationRules.${index}.value`}>
                        {({ field, meta, form }: FieldProps<number | ''>) => (
                          <FormField
                            label={t('grants.forms.questions.validationRules.value')}
                            description={t('grants.forms.questions.validationRules.valueHelp')}
                            status={meta.touched && meta.error ? 'danger' : 'default'}
                            statusMessage={meta.touched && meta.error ? meta.error : undefined}
                          >
                            <TextInput
                              data-testid={`${testIdPrefix}-validation-rule-value-${index}`}
                              type="number"
                              placeholder={t('grants.forms.questions.validationRules.valuePlaceholder')}
                              name={field.name}
                              value={field.value}
                              onChange={e => {
                                const val = e.target.valueAsNumber
                                form.setFieldValue(field.name, Number.isNaN(val) ? '' : val)
                              }}
                              onBlur={field.onBlur}
                              status={meta.touched && meta.error ? 'danger' : 'default'}
                            />
                          </FormField>
                        )}
                      </Field>
                    ) : (
                      <Field name={`validationRules.${index}.candidateField`}>
                        {({ field, meta, form }: FieldProps<string>) => (
                          <FormField
                            label={t('grants.forms.questions.validationRules.candidateField')}
                            status={meta.touched && meta.error ? 'danger' : 'default'}
                            statusMessage={meta.touched && meta.error ? meta.error : undefined}
                          >
                            <Select
                              data-testid={`${testIdPrefix}-validation-rule-field-${index}`}
                              name={field.name}
                              options={CANDIDATE_FIELD_OPTIONS}
                              defaultValue={CANDIDATE_FIELD_OPTIONS.find(o => o.value === field.value)}
                              onChange={e => form.setFieldValue(field.name, e.value)}
                              placeholder={t('grants.forms.questions.validationRules.candidateFieldPlaceholder')}
                            />
                          </FormField>
                        )}
                      </Field>
                    )}
                  </div>

                  <div style={{ flex: 1, minWidth: 160 }}>
                    <Field name={`validationRules.${index}.operator`}>
                      {({ field, meta, form }: FieldProps<string>) => (
                        <FormField
                          label={t('grants.forms.questions.validationRules.operator')}
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <Select
                            data-testid={`${testIdPrefix}-validation-rule-operator-${index}`}
                            name={field.name}
                            options={COMPARISON_OPERATOR_OPTIONS}
                            defaultValue={COMPARISON_OPERATOR_OPTIONS.find(o => o.value === field.value)}
                            onChange={e => form.setFieldValue(field.name, e.value)}
                            placeholder={t('grants.forms.questions.validationRules.operatorPlaceholder')}
                          />
                        </FormField>
                      )}
                    </Field>
                  </div>

                  {rule.type === 'candidate_field_comparison' && (
                    <PercentageField index={index} testIdPrefix={testIdPrefix} />
                  )}
                </Flex>

                <Flex gap="sm" align="center" style={{ marginTop: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <Field name={`validationRules.${index}.errorMessage`}>
                      {({ field }: FieldProps<string>) => (
                        <FormField label={t('grants.forms.questions.validationRules.errorMessage')}>
                          <TextInput
                            data-testid={`${testIdPrefix}-validation-rule-error-${index}`}
                            placeholder={t('grants.forms.questions.validationRules.errorMessagePlaceholder')}
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                          />
                        </FormField>
                      )}
                    </Field>
                  </div>

                  <Button
                    variant="reject"
                    size="small"
                    onClick={() => remove(index)}
                    data-testid={`${testIdPrefix}-remove-validation-rule-${index}`}
                  >
                    {t('button.remove')}
                  </Button>
                </Flex>
              </RuleCard>
            ))}

            <Button
              variant="secondary"
              size="small"
              onClick={() =>
                push({
                  uid: generateUid(),
                  type: 'candidate_field_comparison',
                  candidateField: '',
                  value: '',
                  operator: '',
                  percentage: undefined,
                  errorMessage: ''
                })
              }
              data-testid={`${testIdPrefix}-add-validation-rule-button`}
            >
              {t('grants.forms.questions.validationRules.addButton')}
            </Button>
          </Flex>
        </FormField>
      )}
    </FieldArray>
  )
}
